import { pacificToday } from '../../../shared/dates';
import type { ObjectionType } from '../../../shared/enums';
import { OBJECTION_TYPES } from '../../../shared/enums';
import type { IsoDate, Lead, Objection, Visit } from '../../../shared/types';
import { addDays, diffDays, parseIsoDate } from './dates';
import { bucketFor } from './today';
import type { ContractBucket } from './today';

/** Dashboard math (M5, D36) — pure functions over the mirror; everything works offline. */

export interface DayCount {
  date: IsoDate;
  count: number;
}

/** Last `days` Pacific calendar days (oldest → today), zero-filled. */
export const visitsPerDay = (visits: readonly Visit[], today: IsoDate, days = 14): DayCount[] => {
  const counts = new Map<string, number>();
  for (const v of visits) {
    const key = pacificToday(new Date(v.visited_at));
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const out: DayCount[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = addDays(today, -i);
    out.push({ date, count: counts.get(date) ?? 0 });
  }
  return out;
};

export interface WeekCount {
  /** Monday of the week (local). */
  start: IsoDate;
  count: number;
}

/** Monday of the week containing `date` (sales weeks run Mon–Sun). */
export const weekStart = (date: IsoDate): IsoDate => {
  const d = parseIsoDate(date);
  const dow = d.getDay(); // 0 = Sunday
  return addDays(date, -((dow + 6) % 7));
};

/** Last `weeks` Mon–Sun weeks (oldest → current), zero-filled. */
export const visitsPerWeek = (visits: readonly Visit[], today: IsoDate, weeks = 8): WeekCount[] => {
  const counts = new Map<string, number>();
  for (const v of visits) {
    const key = weekStart(pacificToday(new Date(v.visited_at)));
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const currentStart = weekStart(today);
  const out: WeekCount[] = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const start = addDays(currentStart, -7 * i);
    out.push({ start, count: counts.get(start) ?? 0 });
  }
  return out;
};

export interface WeekToDate {
  thisWeek: number;
  lastWeek: number;
  /** "Mon", "Mon–Wed"… or "week" on Sunday, when both weeks are complete. */
  span: string;
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Monday → today against the same weekdays of last week (`dates` are Pacific days). On a
 *  Wednesday a whole last week would always look better, so the comparison stays like for like. */
export const weekToDate = (dates: readonly IsoDate[], today: IsoDate): WeekToDate => {
  const start = weekStart(today);
  const lastStart = addDays(start, -7);
  const lastEnd = addDays(today, -7);
  let thisWeek = 0;
  let lastWeek = 0;
  for (const d of dates) {
    if (d >= start && d <= today) thisWeek++;
    else if (d >= lastStart && d <= lastEnd) lastWeek++;
  }
  const elapsed = diffDays(today, start);
  const span =
    elapsed === 0 ? 'Mon' : elapsed === 6 ? 'week' : `Mon–${WEEKDAY_SHORT[parseIsoDate(today).getDay()]}`;
  return { thisWeek, lastWeek, span };
};

export interface ObjectionCount {
  type: ObjectionType;
  count: number;
}

export const topObjections = (objections: readonly Objection[], top = 5): ObjectionCount[] => {
  const counts = new Map<ObjectionType, number>();
  for (const o of objections) counts.set(o.type, (counts.get(o.type) ?? 0) + 1);
  return OBJECTION_TYPES.map((type) => ({ type, count: counts.get(type) ?? 0 }))
    .filter((x) => x.count > 0)
    .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type))
    .slice(0, top);
};

export interface ContractSummary {
  total: number;
  byBucket: Record<ContractBucket, number>;
}

/** Contracts ending within 90 days (same rules as Today: expired + closed_won excluded, D9). */
export const contractsExpiring = (leads: readonly Lead[], today: IsoDate): ContractSummary => {
  const byBucket: Record<ContractBucket, number> = { 30: 0, 60: 0, 90: 0 };
  let total = 0;
  for (const lead of leads) {
    if (lead.stage === 'closed_won' || !lead.contract_end_date) continue;
    const bucket = bucketFor(diffDays(lead.contract_end_date, today));
    if (!bucket) continue;
    byBucket[bucket]++;
    total++;
  }
  return { total, byBucket };
};
